'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ScheduleTriggerCallProps {
  contactId: string;
  onCallComplete: () => void;
}

const CONTACT_NAME = 'Margaret';

const ScheduleTriggerCall = ({ contactId, onCallComplete }: ScheduleTriggerCallProps) => {
  const [dailyCallsEnabled, setDailyCallsEnabled] = useState(true);
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [calling, setCalling] = useState(false);
  const [callStatus, setCallStatus] = useState<string | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);

  const handleCallNow = async () => {
    setCalling(true);
    setCallStatus('Initiating call...');

    try {
      const res = await fetch('/api/twilio/check-in-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phoneNumber: phoneNumber.trim() || undefined,
          contactName: CONTACT_NAME,
          contactId: 'contact-1',
          caregiverPhone: phoneNumber.trim() || undefined,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setCallSid(data.callSid);
        setCallStatus(
          `✅ Call started! Margaret's phone is ringing at ${phoneNumber}`
        );

        // Start polling for call status
        pollCallStatus(data.callSid);
      } else {
        setCallStatus(
          `❌ Unable to place call right now — ${data.error?.message || 'check your connection and try again'}`
        );
        setCalling(false);
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      setCallStatus(
        '❌ Unable to place call right now — check your connection and try again'
      );
      setCalling(false);
    }
  };

  const pollCallStatus = async (sid: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`/api/twilio/call-status?callSid=${sid}`);
        const data = await res.json();

        if (data.success) {
          if (data.status === 'completed') {
            setCallStatus(
              `✅ Call completed! Duration: ${Math.floor(data.duration / 60)}m ${data.duration % 60}s. Summary will appear above when processing is complete.`
            );
            clearInterval(pollInterval);
            setCalling(false);
            onCallComplete();
          } else if (data.status === 'in-progress') {
            setCallStatus(
              `📞 Call in progress... Summary will appear here when complete`
            );
          } else if (data.status === 'failed' || data.status === 'busy' || data.status === 'no-answer') {
            setCallStatus(`❌ Call ${data.status}. Please try again later.`);
            clearInterval(pollInterval);
            setCalling(false);
          }
        }
      } catch (error) {
        console.error('Error polling call status:', error);
      }
    }, 10000); // Poll every 10 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (calling) {
        setCallStatus('Call status check timed out. Refresh page to see latest status.');
        setCalling(false);
      }
    }, 300000);
  };

  const handleSaveSchedule = () => {
    alert(`Schedule saved! Daily calls ${dailyCallsEnabled ? 'enabled' : 'disabled'} at ${scheduledTime}`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Daily Automated Calls */}
      <div className="glass p-6">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Daily Automated Calls
        </h2>

        <div className="space-y-4">
          {/* Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-foreground font-medium">Enable daily calls</span>
            <button
              onClick={() => setDailyCallsEnabled(!dailyCallsEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-smooth ${
                dailyCallsEnabled ? 'bg-primary' : 'bg-sage-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-smooth ${
                  dailyCallsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Time Picker */}
          <div>
            <label className="block text-sm text-muted-foreground mb-2">
              Call Margaret at
            </label>
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="w-full px-4 py-2 border border-sage-200/30 rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm text-muted-foreground mb-2">Phone number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="(415) 555-0192"
              className="w-full px-4 py-2 border border-sage-200/30 rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
            <span className="text-foreground">
              {dailyCallsEnabled
                ? `Active — Next call tomorrow ${scheduledTime}`
                : 'Inactive'}
            </span>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveSchedule}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-smooth"
          >
            Save Schedule
          </button>
        </div>
      </div>

      {/* Call Now */}
      <div className="glass p-6">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Call Now
        </h2>

        <div className="space-y-4">
          <p className="text-muted-foreground">
            Conducts a 5-minute AI wellness check
          </p>

          <button
            onClick={handleCallNow}
            disabled={calling || !phoneNumber.trim()}
            className="w-full px-6 py-4 bg-success text-white rounded-lg font-semibold text-lg hover:bg-success/90 transition-smooth flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {calling ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Calling...</span>
              </>
            ) : (
              <>
                <Icon name="PhoneIcon" size={24} />
                <span>📞 Call Margaret Now</span>
              </>
            )}
          </button>

          {/* Call Status */}
          {callStatus && (
            <div
              className={`p-4 rounded-lg border ${
                callStatus.includes('✅')
                  ? 'bg-success/10 border-success/30 text-success' : callStatus.includes('❌')
                  ? 'bg-error/10 border-error/30 text-error' :'bg-primary/10 border-primary/30 text-primary'
              }`}
            >
              <p className="text-sm font-medium">{callStatus}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleTriggerCall;